<script setup lang="ts">
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { BAlert, BButton, BModal } from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';

// --- User Store ---
const userStore = useUserStore();

// --- Component Properties ---

const props = defineProps<{ show: boolean }>();

// --- Component Emits ---
const emit = defineEmits<{ 'update:show': [boolean] }>();

// Bridge prop <-> v-model for the modal
const visible = computed({
  get: () => props.show,
  set: (val: boolean) => emit('update:show', val),
});
</script>
<template>
  <div>
    <BModal v-model="visible" size="lg">
      <template #title>
        <FontAwesomeIcon icon="book" class="me-2" />
        Sampling Human Analytics Cloud - User Guide
      </template>
      <div>
        <BAlert v-model="userStore.noOrganizationSelected" variant="warning">
          <h5>Access Required</h5>
          <p>
            You are not associated with any organization. Please contact your
            account manager to get access to all cloud analytics features first.
            When you have access, this guide will walk you through the standard
            workflow to analyze your data.
          </p>
        </BAlert>
        <h1 class="mt-0">
          <font-awesome-icon icon="dna" class="me-2" />
          DNA/RNA extraction
        </h1>
        <p>
          Begin in the lab by extracting DNA/RNA with a Sampling Human kit.
          Record the kit code/ID and keep it linked to your samples — you'll
          provide this during data upload. Libraries are then prepared and sent
          for sequencing.
        </p>
        <h1>
          <font-awesome-icon icon="upload" class="me-2" />
          Import data
        </h1>
        <p>
          When sequencing is complete, bring the data into the Sampling Human
          Analytics Cloud via
          <router-link :to="{ name: 'UploadManager' }"
            >Upload Manager</router-link
          >:
        </p>
        <ul class="list-unstyled" style="padding-left: 2rem">
          <li>
            <font-awesome-icon icon="upload" class="me-2" />
            Direct file upload
          </li>
          <li>
            <font-awesome-icon icon="server" class="me-2" />
            FTP transfer from your sequencing provider
          </li>
          <li>
            <font-awesome-icon icon="info-circle" class="me-2" />
            Import from Illumina BaseSpace Sequence Hub (BSSH)
          </li>
        </ul>
        <p>
          During upload, you'll be prompted for the kit code used for
          extraction.
        </p>

        <h1>
          <font-awesome-icon icon="flask" class="me-2" />
          Sample annotation
        </h1>

        <p>
          After upload, annotate samples with metadata in
          <router-link :to="{ name: 'DataManager' }">Data Manager</router-link>,
          or enable Auto-annotation during upload to fill fields automatically.
          You can edit annotations later. Samples imported from BSSH are pre-
          annotated from BSSH metadata where available.
        </p>

        <h1>
          <font-awesome-icon icon="list" class="me-2" />
          Projects
        </h1>

        <p>
          Processing happens inside projects, not in Data Manager. Create a
          project in
          <router-link :to="{ name: 'Projects' }">Project Manager</router-link>,
          then associate one or more samples (from one or multiple collections).
          If you enabled it during upload, projects and associations can be
          created automatically. You can list and manage projects from
          <router-link :to="{ name: 'Projects' }">Project Manager</router-link>.
        </p>

        <h1>
          <font-awesome-icon icon="wand-magic-sparkles" class="me-2" />
          Sample reconstruction
        </h1>

        <p>
          Open a project and start a Sample Reconstruction task from the
          <router-link :to="{ name: 'Projects' }">Project Detail</router-link>
          view. Confirm the correct kit code—this is essential for accurate
          analysis. Runtime depends on data size and complexity. If desired,
          enable email notifications in your profile to be alerted when the task
          finishes.
        </p>

        <h1>
          <font-awesome-icon icon="line-chart" class="me-2" />
          Review results
        </h1>

        <p>
          When reconstruction completes, view outputs in the
          <router-link :to="{ name: 'Projects' }">Project Detail</router-link>
          view. Browse files, inspect progress and metrics, and download results
          for further analysis.
        </p>
      </div>

      <!-- Modal footer with close button -->
      <template #footer>
        <BButton variant="secondary" @click="visible = false">Close</BButton>
      </template>
    </BModal>
  </div>
</template>
<style scoped>
h1 {
  font-size: 1.5rem;
  margin-top: 2rem;
}
h2 {
  font-size: 1.3rem;
}
h3 {
  font-size: 1.1rem;
}
h4 {
  font-size: 0.9rem;
}
h5 {
  font-size: 0.7rem;
}
</style>
